import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { convertTimestampsToISO } from '../../utils/firebaseUtils.js';
import {
  getPatient as getPatientByIdService,
  getAllPatients as getAllPatientsService
} from '../../services/patientService';

export const fetchPatientDetails = createAsyncThunk(
  'patients/fetchPatientDetails',
  async (patientId) => {
    const patientData = await getPatientByIdService(patientId);
    return convertTimestampsToISO(patientData);
  }
);


export const fetchPatients = createAsyncThunk(
  'patients/fetchPatients',
  async (facilityId, { getState, rejectWithValue }) => {
    try {
      if (!facilityId) {
        // Try to get facilityId from auth state if not provided
        const state = getState();
        facilityId = state.auth.user?.defaultFacility;
        
        if (!facilityId) {
          throw new Error('facilityId is required to fetch patients');
        }
      }
      
      const patients = await getAllPatientsService({ facilityId });
      return convertTimestampsToISO(patients);
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  patients: {},
  selectedPatient: null,
  loading: false,
  error: null,
};

const patientsSlice = createSlice({
  name: 'patients',
  initialState,
  reducers: {
    setSelectedPatient: (state, action) => {
      state.selectedPatient = action.payload;
    },
    clearPatients: (state) => {
      state.patients = [];
      state.selectedPatient = null;
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all patients
      .addCase(fetchPatients.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatients.fulfilled, (state, action) => {
        state.loading = false;
        // Convert array to object with patient IDs as keys
        state.patients = action.payload.reduce((acc, patient) => {
          acc[patient.id] = patient;
          return acc;
        }, {});
      })
      .addCase(fetchPatients.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      })
      // Fetch patient details
      .addCase(fetchPatientDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPatientDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedPatient = action.payload;
        // Also update the patients state to prevent refetching
        state.patients[action.payload.id] = action.payload;
      })
      .addCase(fetchPatientDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

export const { setSelectedPatient, clearPatients } = patientsSlice.actions;

// Selectors
export const selectPatients = (state) => Object.values(state.patients.patients || {});
export const selectPatientsLoading = (state) => state.patients.loading;
export const selectPatientsError = (state) => state.patients.error;
export const selectSelectedPatient = (state) => state.patients.selectedPatient;

export default patientsSlice.reducer;
